// A React error boundary — the dashboard had none, so a single throwing module
// (e.g. a map effect) unmounted the whole app to a blank page. Wrap each overview
// module in this: a crash is contained to that panel, the rest of the dashboard
// stands, and the error is logged (not swallowed) so it stays diagnosable.
import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  /** Shown when the child throws. Defaults to a neutral "panel hit a snag" card. */
  fallback?: ReactNode;
  /** Identifies the boundary in the console log (e.g. the module kind). */
  label?: string;
}

interface State {
  failed: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { failed: false };

  static getDerivedStateFromError(): State {
    return { failed: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error(`[dashboard] "${this.props.label ?? 'panel'}" crashed — contained`, error, info);
  }

  render(): ReactNode {
    if (this.state.failed) {
      return (
        this.props.fallback ?? (
          <div className="overview-coming-card" role="status">
            <strong>This panel couldn’t render.</strong>
            <p>The rest of your dashboard is unaffected — try reloading.</p>
          </div>
        )
      );
    }
    return this.props.children;
  }
}
